import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useOptionalAccountConnectionScope } from "../components/moduleExperience/AccountConnectionScopeContext";
import {
  getCachedSessionUser,
  SESSION_CHANGED_EVENT,
} from "../components/pages/backend_url";
import {
  detectBrowserTimeZone,
  isValidIanaTimeZone,
  UTC_TIME_ZONE,
} from "./timeZones";
import { useOptionalLanguage } from "../localization/LanguageContext";

export const DISPLAY_TIME_ZONE_STORAGE_PREFIX = "crm.display-time-zone.v1";
export const DISPLAY_TIME_ZONE_MODES = Object.freeze([
  "account",
  "connection",
  "device",
  "fixed",
]);

const DISPLAY_TIME_ZONE_MODE_SET = new Set(DISPLAY_TIME_ZONE_MODES);
// A new account/member session begins with the customer account's configured
// timezone. Connection, device and fixed modes are deliberate view choices;
// they must never silently override the account's product-wide default.
const DEFAULT_PREFERENCE = Object.freeze({ mode: "account", timeZone: "" });

const clean = (value) => String(value ?? "").trim();

/** Validate presentation-only IANA names without changing aliases or offsets. */
export function validDisplayTimeZone(value) {
  const timeZone = clean(value);
  return timeZone.length <= 128 && isValidIanaTimeZone(timeZone)
    ? timeZone
    : "";
}

function validDisplayLocale(value) {
  const locale = clean(value);
  if (!locale || locale.length > 64) return "";

  try {
    return Intl.getCanonicalLocales(locale)[0] || "";
  } catch {
    return "";
  }
}

export function deviceDisplayTimeZone() {
  return validDisplayTimeZone(detectBrowserTimeZone());
}

/**
 * Preferences are qualified by opaque tenant and user identifiers. Email
 * addresses and timezone values never appear in browser URLs.
 */
export function displayTimeZoneStorageKey(tenantId, userId) {
  const tenant = clean(tenantId);
  const user = clean(userId);
  if (!tenant || !user) return "";
  return `${DISPLAY_TIME_ZONE_STORAGE_PREFIX}:${encodeURIComponent(tenant)}:${encodeURIComponent(user)}`;
}

function browserSessionStorage() {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

export function normalizeDisplayTimeZonePreference(value) {
  const mode = clean(value?.mode).toLowerCase();
  if (!DISPLAY_TIME_ZONE_MODE_SET.has(mode)) return null;

  if (mode === "fixed") {
    const timeZone = validDisplayTimeZone(value?.timeZone || value?.timezone_name);
    return timeZone ? { mode, timeZone } : null;
  }

  return { mode, timeZone: "" };
}

export function readDisplayTimeZonePreference(
  storageKey,
  storage = browserSessionStorage()
) {
  if (!storageKey || !storage) return { ...DEFAULT_PREFERENCE };

  try {
    const raw = storage.getItem(storageKey);
    if (!raw) return { ...DEFAULT_PREFERENCE };
    const preference = normalizeDisplayTimeZonePreference(JSON.parse(raw));
    if (preference) return preference;
    storage.removeItem(storageKey);
  } catch {
    try {
      storage.removeItem(storageKey);
    } catch {
      // Restricted browser storage falls back to the in-memory default.
    }
  }

  return { ...DEFAULT_PREFERENCE };
}

function persistDisplayTimeZonePreference(
  storageKey,
  preference,
  storage = browserSessionStorage()
) {
  if (!storageKey || !storage) return;
  try {
    storage.setItem(storageKey, JSON.stringify(preference));
  } catch {
    // Display conversion remains available when persistence is blocked.
  }
}

function cachedPrincipal() {
  const user = getCachedSessionUser();
  return user && typeof user === "object" ? user : {};
}

function exactConnectionTimeZone(scope) {
  const context = scope?.context;
  const selectedConnectionId = clean(scope?.selectedConnectionId);
  if (
    scope?.status !== "ready" ||
    context?.mode !== "single" ||
    !selectedConnectionId ||
    clean(context.selected_connection_id) !== selectedConnectionId
  ) {
    return "";
  }

  const selected = (Array.isArray(scope?.options) ? scope.options : []).find(
    (option) => clean(option?.id) === selectedConnectionId
  );
  return validDisplayTimeZone(selected?.timezone_name);
}

function resolveDisplayTimeZone({
  preference,
  connectionTimeZone,
  accountTimeZone,
  deviceTimeZone,
  forceAccount,
}) {
  if (forceAccount) {
    return accountTimeZone
      ? { timeZone: accountTimeZone, source: "account" }
      : { timeZone: UTC_TIME_ZONE, source: "utc" };
  }

  const explicitCandidates = {
    account: accountTimeZone,
    connection: connectionTimeZone,
    device: deviceTimeZone,
    fixed: validDisplayTimeZone(preference.timeZone),
  };
  const explicit = validDisplayTimeZone(explicitCandidates[preference.mode]);
  if (explicit) return { timeZone: explicit, source: preference.mode };
  if (connectionTimeZone) {
    return { timeZone: connectionTimeZone, source: "connection" };
  }
  if (accountTimeZone) return { timeZone: accountTimeZone, source: "account" };
  return { timeZone: UTC_TIME_ZONE, source: "utc" };
}

const defaultController = Object.freeze({
  mode: "account",
  fixedTimeZone: "",
  timeZone: UTC_TIME_ZONE,
  locale: "en-US",
  source: "utc",
  accountTimeZone: "",
  connectionTimeZone: "",
  deviceTimeZone: "",
  forcedToAccount: false,
  setPreference: () => false,
});

const DisplayTimeZoneContext = createContext(defaultController);

export function DisplayTimeZoneProvider({ children }) {
  const connectionScope = useOptionalAccountConnectionScope();
  const productLanguage = useOptionalLanguage();
  const [principal, setPrincipal] = useState(cachedPrincipal);
  const storageKey = displayTimeZoneStorageKey(
    principal?.tenantId,
    principal?.userId
  );
  const [storedPreference, setStoredPreference] = useState(() => ({
    storageKey,
    value: readDisplayTimeZonePreference(storageKey),
  }));

  useEffect(() => {
    const refreshPrincipal = (event) => {
      setPrincipal(event?.detail?.authenticated === false ? {} : cachedPrincipal());
    };
    window.addEventListener(SESSION_CHANGED_EVENT, refreshPrincipal);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, refreshPrincipal);
  }, []);

  useEffect(() => {
    setStoredPreference({
      storageKey,
      value: readDisplayTimeZonePreference(storageKey),
    });
  }, [storageKey]);

  // Never render the previous membership's preference while a session change
  // is being reconciled by the effect above.
  const preference = storedPreference.storageKey === storageKey
    ? storedPreference.value
    : DEFAULT_PREFERENCE;

  const accountTimeZone = validDisplayTimeZone(
    principal?.tenantDefaultTimezone
  );
  // The account locale remains the default until a person chooses a product
  // language. The chosen language then governs date/number presentation too,
  // while all stored instants and account timezone rules remain unchanged.
  const locale = validDisplayLocale(
    productLanguage?.hasExplicitLanguage ? productLanguage.locale : ""
  )
    || validDisplayLocale(principal?.tenantDefaultLocale)
    || "en-US";
  const connectionTimeZone = exactConnectionTimeZone(connectionScope);
  const deviceTimeZone = deviceDisplayTimeZone();
  const forcedToAccount = connectionScope?.context?.mode === "all_permitted";
  const resolved = resolveDisplayTimeZone({
    preference,
    connectionTimeZone,
    accountTimeZone,
    deviceTimeZone,
    forceAccount: forcedToAccount,
  });

  const setPreference = useCallback((nextValue) => {
    const normalized = normalizeDisplayTimeZonePreference(nextValue);
    if (!normalized) return false;
    setStoredPreference({ storageKey, value: normalized });
    persistDisplayTimeZonePreference(storageKey, normalized);
    return true;
  }, [storageKey]);

  const controller = useMemo(() => ({
    mode: preference.mode,
    fixedTimeZone: preference.timeZone,
    timeZone: resolved.timeZone,
    locale,
    source: resolved.source,
    accountTimeZone,
    connectionTimeZone,
    deviceTimeZone,
    forcedToAccount,
    setPreference,
  }), [
    accountTimeZone,
    connectionTimeZone,
    deviceTimeZone,
    forcedToAccount,
    locale,
    preference.mode,
    preference.timeZone,
    resolved.source,
    resolved.timeZone,
    setPreference,
  ]);

  return (
    <DisplayTimeZoneContext.Provider value={controller}>
      {children}
    </DisplayTimeZoneContext.Provider>
  );
}

export function useDisplayTimeZone() {
  return useContext(DisplayTimeZoneContext);
}
